import { Map, Record } from 'immutable';

import { MutationType, OwnerIdType } from './';

import { MetaStateType } from './meta';

import { MediaWebPublishingType } from './web-publishing';

export enum MediumState {
  NEW = 'new',
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  ERRORED = 'errored'
}

export enum MediumType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  UNKNOWN = 'unknown'
}

export enum MediaInclusionType {
  NONE = 'none',
  PRIMARY = 'primary',
  ALL = 'all'
}

export enum MediaBasename {
  UUID = 'uuid',
  OBJECTID = 'objectid',
  NAME = 'name',
  ORIGINAL = 'original'
}

export const IMAGE_SIZE_THUMBNAIL = 200;
export const IMAGE_SIZE_SMALL = 768;
export const IMAGE_SIZE_MEDIUM = 992;
export const IMAGE_SIZE_LARGE = 1200;

export const IMAGE_SIZE_MAX = 5000;

export const IMAGE_QUALITY = 80; // ???: Sharp defaults this to 80 ???:
export const FUZZY_IMAGE_QUALITY = 10;
export const FULL_IMAGE_QUALITY = 100;
export const PDF_QUALITY = 75;

export interface IDimensions {
  width: number;
  height: number;
}

export interface IRectangle {
  x: number;
  y: number;
  width: number; // can be pixels or percent
  height: number; // can be pixels or percent
}

export interface MediumExtent {
  start: number;
  end: number;
}

export interface MediaTransform {
  rotation?: number;
  relativeCrop?: IRectangle;
  relativeThumbnail?: IRectangle;
}

export interface IDataURLMedium {
  dataURL: string;
  width: number;
  height: number;
}

export interface MediaPropsType {
  name?: string;
  description?: string;
  type: MediumType;
  mutationType?: MutationType;
}

export interface PhotoMediaPropsType extends MediaPropsType {
  filename?: string;
  transform?: MediaTransform;
}

export interface ProcessedPhotoPropsType extends PhotoMediaPropsType {
  key: string; // ??? how is key used ???
  file?: File;
  canvas: HTMLCanvasElement;
  dataURL?: string;
}

export interface EditedImageProps {
  preview: IDataURLMedium;
  thumbnail: IDataURLMedium;
  transform: MediaTransform;
}

// thumbnail and transform are optional;  preview is required
export interface EditedProcessedPhotoPropsType
  extends ProcessedPhotoPropsType,
    Omit<EditedImageProps, 'thumbnail' | 'transform'> {
  thumbnail?: IDataURLMedium;
  transform?: MediaTransform;
}

export interface DocumentMediaPropsType extends MediaPropsType {
  filename?: string;
}

export interface ProcessedDocumentPropsType extends DocumentMediaPropsType {
  key: string;
  file: File;
}

export interface VideoMediaPropsType extends MediaPropsType {
  filename?: string;
}

export interface ProcessedVideoPropsType extends VideoMediaPropsType {
  key: string;
  file: File;
}

export interface AudioMediaPropsType extends MediaPropsType {
  filename?: string;
}

export interface ProcessedAudioPropsType extends AudioMediaPropsType {
  key: string;
  file: File;
}

export type ProcessedMedium =
  | EditedProcessedPhotoPropsType
  | ProcessedPhotoPropsType
  | ProcessedDocumentPropsType
  | ProcessedVideoPropsType
  | ProcessedAudioPropsType;

// hasMedia and "change-set" representation between client and server...  Encapsulating these
// properties as a unit of change allows for whole entry (including media) save semantics.
export interface IMediumDescriptor {
  muid: string;

  name?: string;
  description?: string;
  transform?: MediaTransform;

  preview?: HTMLCanvasElement;
  thumbnail?: HTMLCanvasElement;
}

export class MediumDescriptorRecord extends Record<IMediumDescriptor>({
  muid: '',

  name: undefined,
  description: undefined,
  transform: undefined,

  preview: undefined,
  thumbnail: undefined
}) {
  toJS(): IMediumDescriptor {
    return super.toJS() as IMediumDescriptor;
  }
}

export type MediumDerivativeType =
  | 'edit'
  | 'full'
  | 'full_sq'
  | 'thumbnail'
  | 'small'
  | 'small_fz'
  | 'small_sq'
  | 'large'
  | 'large_fz'
  | 'large_sq'
  | 'public_thumbnail'
  | 'public'
  | 'public_highres'
  | 'public_original'
  | 'video'
  | 'audio';

export interface DerivativeMediumBaseType {
  path: string;
}

export interface DerivativeMediumType extends DerivativeMediumBaseType {
  width: number;
  height: number;
}

/**
 * DerivativeMediumRecord
 */
export class DerivativeMediumRecord extends Record<DerivativeMediumType>({
  path: '',
  width: 0,
  height: 0
}) {
  public toJS(): DerivativeMediumType {
    return super.toJS() as DerivativeMediumType;
  }
}

export interface DerivativeVideoType extends DerivativeMediumBaseType {}

export interface DerivativeAudioType extends DerivativeMediumBaseType {}

export type DerivativeMediaDictionaryPropertyType =
  | DerivativeMediumType
  | DerivativeVideoType
  | DerivativeAudioType
  | string
  | boolean
  | undefined;

export interface DerivativeMediaDictionaryType {
  _complete?: boolean;

  edit?: DerivativeMediumType;

  full?: DerivativeMediumType;
  full_sq?: DerivativeMediumType;

  thumbnail?: DerivativeMediumType;
  thumbnail_2x?: DerivativeMediumType;

  small?: DerivativeMediumType;
  small_2x?: DerivativeMediumType;
  small_fz?: DerivativeMediumType;

  small_sq?: DerivativeMediumType;
  small_sq_2x?: DerivativeMediumType;

  // medium?: DerivativeMediumType;
  // medium_2x?: DerivativeMediumType;
  // medium_fz?: DerivativeMediumType;

  large?: DerivativeMediumType;
  large_2x?: DerivativeMediumType;
  large_fz?: DerivativeMediumType;

  large_sq?: DerivativeMediumType;
  large_sq_2x?: DerivativeMediumType;

  public_thumbnail?: DerivativeMediumType;
  public_thumbnail_2x?: DerivativeMediumType;

  public?: DerivativeMediumType;
  public_2x?: DerivativeMediumType;

  public_highres?: DerivativeMediumType;
  public_highres_2x?: DerivativeMediumType;

  public_original?: DerivativeMediumBaseType; // path only; not width/height

  dominant_color?: string;
  complementary_color?: string;

  video?: DerivativeVideoType;
  audio?: DerivativeAudioType;

  [key: string]: DerivativeMediaDictionaryPropertyType;
}

export const PUBLIC_MEDIA_PROPS = [
  'public_thumbnail',
  'public_thumbnail_2x',
  'public',
  'public_2x',
  'public_highres',
  'public_highres_2x'
];

export const DERIVATIVE_MEDIA_PATHS = [
  'edit',
  'full',
  'full_sq',
  'thumbnail',
  'thumbnail_2x',
  'small',
  'small_2x',
  'small_fz',
  'small_sq',
  'small_sq_2x',
  'large',
  'large_2x',
  'large_fz',
  'large_sq',
  'large_sq_2x'
];

export interface MediaAttributes {
  uuid: string;
  account_id?: string;

  state?: MediumState;

  entry_uuid?: string;
  individual_id?: string;

  owner_id?: string | null;
  owner_type?: OwnerIdType | null;

  name?: string | null;
  description?: string | null;
  path: string;
  content_type: string;
  original_filename?: string;
  width: number;
  height: number;
  order: number; // TODO: this needs to go away-- will be represented by placement in hasMedia list
  extent?: MediumExtent | null;
  transform?: MediaTransform | null;
  meta_info?: DerivativeMediaDictionaryType | null;
  created_by?: string;
  created_at: Date;
  updated_by?: string;
  updated_at: Date;

  web_publishing?: MediaWebPublishingType | null;

  error_detail?: string | null;
}

export interface MediaAPIResponse
  extends Omit<MediaAttributes, 'created_at' | 'updated_at'> {
  _partial?: boolean;
  percent_complete?: number; // TODO: remove after conversion to client direct upload to S3

  created_at: string;
  updated_at: string;
}

export interface MediaUploadRequestAPIResponse
  extends Omit<MediaAttributes, 'created_at' | 'updated_at'> {
  url: string;

  created_at: string;
  updated_at: string;
}

// MediaResponseType is the response type from "derived" sources (like Elasticsearch) and direct
// extracts from the media table from searchs.  It's also used in the HUB (i.e from Elasticsearch)
//
export interface MediaResponseType {
  uuid: string;
  state?: MediumState;
  content_type: string;
  path: string;

  extent?: MediumExtent;
  name?: string; // aka "caption"
  description?: string;
  meta_info?: DerivativeMediaDictionaryType; // deprecated; use derivatives instead
  derivatives?: DerivativeMediaDictionaryType;

  _partial?: boolean;
}

export type PublicMediaResponseType = Omit<
  MediaResponseType,
  'path' | 'state' | 'meta_info'
>;

export interface MediaClientModel extends MediaAPIResponse {
  _meta?: MetaStateType;
  _error?: any;

  _previewDataURL?: IDataURLMedium;
  _thumbnailDataURL?: IDataURLMedium;
  _localFile?: File | Blob;

  _info?: PhotoMediaPropsType | DocumentMediaPropsType;

  filename?: string;
}

export class MediumRecord extends Record<MediaClientModel>({
  uuid: '',
  account_id: undefined,

  entry_uuid: undefined,
  individual_id: undefined,
  owner_id: undefined,
  owner_type: undefined,

  content_type: '',
  original_filename: undefined,
  path: '',
  extent: undefined,
  width: 0,
  height: 0,

  meta_info: undefined,

  name: undefined,
  description: undefined,
  order: 0,
  transform: undefined,
  web_publishing: undefined,

  percent_complete: undefined,

  created_by: undefined,
  created_at: '',
  updated_by: undefined,
  updated_at: '',

  _previewDataURL: undefined,
  _thumbnailDataURL: undefined,

  _info: undefined,

  _meta: undefined,
  _partial: undefined,
  _error: undefined
}) {
  toJS(): MediaClientModel {
    return super.toJS() as MediaClientModel;
  }
}

export type MediumModel = MediumRecord;

export type MediaStateType = Map<string, MediumRecord>;
